/**
 * This file is where we do "rehydration" of your RootStore from AsyncStorage.
 * This lets you persist your state between app launches.
 *
 * Navigation state persistence is handled in navigationUtilities.tsx.
 *
 * Note that Fast Refresh doesn't play well with this file, so if you edit this,
 * do a full refresh of your app instead.
 *
 * @refresh reset
 */
import { applySnapshot, IDisposer, onSnapshot, setLivelinessChecking, unprotect } from "mobx-state-tree"
import { AppState } from "react-native"
import type { RootStore } from "../RootStore"
import type { ItemsStoreSnapshotOut } from "../ItemsStore"
import * as storage from "../../utils/storage"


setLivelinessChecking("ignore")
const yieldToEventLoop = () => new Promise((resolve) => setTimeout(resolve, 16));
/**
 * The key we'll be saving our state as within async storage.
 */

const AUTHENTICATIONSTORE_STATE_STORAGE_KEY = "authStore-v1"
const FORMSSTORE_STATE_STORAGE_KEY = "formsStore-v1"
const INTEGRATIONSSTORE_STATE_STORAGE_KEY = "IntegrationsStore-v1"
const ITEMSSSTORE_STATE_STORAGE_KEY = "ItemsStore-v1"
const TASKSSTORE_STATE_STORAGE_KEY = "TasksStore-v1"
const ITEMS_STORE_SAVE_DEBOUNCE_MS = 300
const ITEMS_STORE_SAVE_MAX_WAIT_MS = 1000
/**
 * Setup the root state.
 */
let _disposerAuthStore: IDisposer
let _disposerFormsStore: IDisposer
let _disposerIntegrationsStore: IDisposer
let _disposerItemsStore: IDisposer
let _disposerTasksStore: IDisposer
export async function setupRootStore(rootStore: RootStore) {
  let restoredState: any

  try {
    unprotect(rootStore)
    // 1. Cargar AuthenticationStore desde sessionCacheStorage (con migración desde mainStorage si es necesario)
    restoredState = await storage.loadSession(AUTHENTICATIONSTORE_STATE_STORAGE_KEY)
    if (!restoredState || Object.keys(restoredState).length === 0) {
      const legacyAuth = await storage.load(AUTHENTICATIONSTORE_STATE_STORAGE_KEY)
      if (legacyAuth && Object.keys(legacyAuth).length > 0) {
        restoredState = legacyAuth
        await storage.saveSession(AUTHENTICATIONSTORE_STATE_STORAGE_KEY, legacyAuth)
        await storage.removeItem(AUTHENTICATIONSTORE_STATE_STORAGE_KEY)
      } else {
        restoredState = {}
      }
    }
    applySnapshot(rootStore.authenticationStore, restoredState)

    // 2. Cargar los otros modelos desde mainStorage
    restoredState = (await storage.load(FORMSSTORE_STATE_STORAGE_KEY)) || {}
    applySnapshot(rootStore.formsStore, restoredState)

    restoredState = (await storage.load(INTEGRATIONSSTORE_STATE_STORAGE_KEY)) || {}
    applySnapshot(rootStore.integrationsStore, restoredState)
    await yieldToEventLoop()
    restoredState = (await storage.load(ITEMSSSTORE_STATE_STORAGE_KEY)) || {}
    await yieldToEventLoop()
    console.log("applySnapshot(rootStore.itemsStore, restoredState) 1");
    await applySnapshot(rootStore.itemsStore, restoredState)
    restoredState = (await storage.load(TASKSSTORE_STATE_STORAGE_KEY)) || {}
    console.log("applySnapshot(rootStore.itemsStore, restoredState) 2");
    await yieldToEventLoop()
    applySnapshot(rootStore.tasksStore, restoredState)
   
  } catch (e) {
    // if there's any problems loading, then inform the dev what happened
    if (__DEV__) {
      //console.tron.error(e.message, null)
    }
  }

  // stop tracking state changes if we've already setup
  if (_disposerAuthStore) _disposerAuthStore()
  if (_disposerFormsStore) _disposerFormsStore()
  if (_disposerIntegrationsStore) _disposerIntegrationsStore()
  if (_disposerItemsStore) _disposerItemsStore()
  if (_disposerTasksStore) _disposerTasksStore()
  // track changes:
  // authenticationStore guarda en sessionCacheStorage
  _disposerAuthStore = onSnapshot(rootStore.authenticationStore, (snapshot) => storage.saveSession(AUTHENTICATIONSTORE_STATE_STORAGE_KEY, snapshot))
  // los otros modelos guardan en mainStorage
  _disposerFormsStore = onSnapshot(rootStore.formsStore, (snapshot) => storage.save(FORMSSTORE_STATE_STORAGE_KEY, snapshot))
  _disposerIntegrationsStore = onSnapshot(rootStore.integrationsStore, (snapshot) => storage.save(INTEGRATIONSSTORE_STATE_STORAGE_KEY, snapshot))
  let pendingItemsSnapshot: ItemsStoreSnapshotOut | undefined
  let itemsSaveTimer: ReturnType<typeof setTimeout> | undefined
  let itemsSaveMaxWaitTimer: ReturnType<typeof setTimeout> | undefined
  let itemsSaveInProgress: Promise<void> | undefined

  const clearItemsSaveTimers = () => {
    if (itemsSaveTimer !== undefined) clearTimeout(itemsSaveTimer)
    if (itemsSaveMaxWaitTimer !== undefined) clearTimeout(itemsSaveMaxWaitTimer)
    itemsSaveTimer = undefined
    itemsSaveMaxWaitTimer = undefined
  }

  const savePendingItemsSnapshot = async (flushAll = false): Promise<void> => {
    clearItemsSaveTimers()
    while (itemsSaveInProgress) await itemsSaveInProgress

    const snapshot = pendingItemsSnapshot
    if (snapshot === undefined) return
    pendingItemsSnapshot = undefined

    itemsSaveInProgress = storage.save(ITEMSSSTORE_STATE_STORAGE_KEY, snapshot).then((saved) => {
      if (!saved) {
        console.error(`No se pudo guardar el snapshot de ${ITEMSSSTORE_STATE_STORAGE_KEY}.`)
      }
    }).finally(() => {
      itemsSaveInProgress = undefined
    })
    await itemsSaveInProgress

    if (pendingItemsSnapshot !== undefined) {
      if (flushAll) {
        await savePendingItemsSnapshot(true)
      } else {
        scheduleItemsSave()
      }
    }
  }

  const scheduleItemsSave = () => {
    if (itemsSaveTimer !== undefined) clearTimeout(itemsSaveTimer)
    itemsSaveTimer = setTimeout(() => {
      void savePendingItemsSnapshot()
    }, ITEMS_STORE_SAVE_DEBOUNCE_MS)
    if (itemsSaveMaxWaitTimer === undefined) {
      itemsSaveMaxWaitTimer = setTimeout(() => {
        void savePendingItemsSnapshot()
      }, ITEMS_STORE_SAVE_MAX_WAIT_MS)
    }
  }

  _disposerItemsStore = onSnapshot(rootStore.itemsStore, (snapshot: ItemsStoreSnapshotOut) => {
    pendingItemsSnapshot = snapshot
    scheduleItemsSave()
  })
  const appStateSubscription = AppState.addEventListener("change", (state) => {
    if (state !== "active") void savePendingItemsSnapshot(true)
  })
  _disposerTasksStore = onSnapshot(rootStore.tasksStore, (snapshot) => storage.save(TASKSSTORE_STATE_STORAGE_KEY, snapshot))

  const unsubscribe = () => {
    appStateSubscription.remove()
    clearItemsSaveTimers()
    _disposerAuthStore()
    _disposerAuthStore = undefined
    _disposerFormsStore()
    _disposerFormsStore = undefined
    _disposerIntegrationsStore()
    _disposerIntegrationsStore = undefined
    _disposerItemsStore()
    _disposerItemsStore = undefined
    _disposerTasksStore()
    _disposerTasksStore = undefined
    void savePendingItemsSnapshot(true)
  }

  return { rootStore, restoredState, unsubscribe }
}
